import { useEffect, useState } from "react";
import { Loader2, Save, Lock, Plus, X, BadgeCheck } from "lucide-react";
import { toast } from "react-toastify";
import { getPerfil, updatePerfil } from "../../services/perfilApi";
import ProfilePictureUploader from "./ProfilePictureUploader";

const VERTICAL_OPTIONS = [
  ["UNDEFINED", "Não definido"],
  ["AUTO", "Automotivo"],
  ["BEAUTY", "Beleza, Spa & Salão"],
  ["APPAREL", "Vestuário"],
  ["EDU", "Educação"],
  ["ENTERTAIN", "Entretenimento"],
  ["EVENT_PLAN", "Planejamento de eventos"],
  ["FINANCE", "Finanças & Bancos"],
  ["GROCERY", "Mercearia"],
  ["GOVT", "Governo"],
  ["HOTEL", "Hotel"],
  ["HEALTH", "Saúde & Bem-estar"],
  ["NONPROFIT", "Sem fins lucrativos"],
  ["PROF_SERVICES", "Serviços profissionais"],
  ["RETAIL", "Varejo"],
  ["TRAVEL", "Viagens & Transporte"],
  ["RESTAURANT", "Restaurante"],
  ["NOT_A_BIZ", "Não é um negócio"],
  ["OTHER", "Outro"],
];

export default function PerfilForm({ contaId }) {
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [about, setAbout] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [vertical, setVertical] = useState("UNDEFINED");
  const [websites, setWebsites] = useState([""]);

  async function carregar() {
    setLoading(true);
    try {
      const p = await getPerfil(contaId);
      setPerfil(p);
      setAbout(p.about || "");
      setDescription(p.description || "");
      setAddress(p.address || "");
      setEmail(p.email || "");
      setVertical(p.vertical || "UNDEFINED");
      const ws = Array.isArray(p.websites) ? p.websites : [];
      setWebsites(ws.length > 0 ? ws : [""]);
    } catch (err) {
      toast.error("Falha ao carregar perfil: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    carregar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contaId]);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const fields = {
        about,
        description,
        address,
        email,
        vertical,
        websites: websites.map((w) => w.trim()).filter(Boolean),
      };
      const updated = await updatePerfil(contaId, fields);
      setPerfil(updated);
      toast.success("Perfil atualizado");
    } catch (err) {
      toast.error("Falha ao salvar: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  }

  function updateWebsite(i, value) {
    setWebsites((prev) => prev.map((w, idx) => (idx === i ? value : w)));
  }

  function addWebsite() {
    if (websites.length >= 2) return;
    setWebsites((prev) => [...prev, ""]);
  }

  function removeWebsite(i) {
    setWebsites((prev) => prev.filter((_, idx) => idx !== i));
  }

  if (loading) {
    return (
      <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-12 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-ink-4" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProfilePictureUploader
        contaId={contaId}
        currentUrl={perfil?.profile_picture_url}
        onUpdated={(p) => setPerfil(p)}
      />

      <div className="bg-surface-1 border border-white/[0.06] rounded-lg p-5 space-y-3">
        <h4 className="text-sm font-semibold text-white flex items-center gap-2">
          <Lock className="w-4 h-4 text-ink-4" /> Identidade verificada (read-only)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="text-xs text-ink-4 mb-1">Telefone</div>
            <div className="text-ink-2">{perfil?.display_phone_number || "—"}</div>
          </div>
          <div>
            <div className="text-xs text-ink-4 mb-1 flex items-center gap-1">
              <BadgeCheck className="w-3 h-3" /> Nome verificado
            </div>
            <div className="text-ink-2">{perfil?.verified_name || "—"}</div>
          </div>
        </div>
        <p className="text-xs text-ink-5 italic">
          Pra mudar o nome verificado é preciso solicitar Display Name Review pela Meta Business Manager.
        </p>
      </div>

      <form onSubmit={handleSave} className="bg-surface-1 border border-white/[0.06] rounded-lg p-5 space-y-4">
        <h4 className="text-sm font-semibold text-white">Informações de negócio</h4>

        <div>
          <label className="text-xs font-medium text-ink-3 block mb-1">Sobre (max 139)</label>
          <input
            type="text"
            value={about}
            onChange={(e) => setAbout(e.target.value.slice(0, 139))}
            maxLength={139}
            placeholder="Frase curta que aparece no topo do perfil"
            className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
          />
          <div className="text-xs text-ink-5 mt-1">{about.length}/139</div>
        </div>

        <div>
          <label className="text-xs font-medium text-ink-3 block mb-1">Descrição (max 512)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 512))}
            rows={4}
            maxLength={512}
            placeholder="Descreva seu negócio em mais detalhes"
            className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5 resize-none"
          />
          <div className="text-xs text-ink-5 mt-1">{description.length}/512</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-ink-3 block mb-1">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="contato@suaempresa.com"
              className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-ink-3 block mb-1">Categoria</label>
            <select
              value={vertical}
              onChange={(e) => setVertical(e.target.value)}
              className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2"
            >
              {VERTICAL_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-ink-3 block mb-1">Endereço</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Rua Exemplo, 100 — São Paulo, SP"
            className="w-full bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-ink-3">Sites (até 2)</label>
            <button
              type="button"
              onClick={addWebsite}
              disabled={websites.length >= 2}
              className="text-xs inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 disabled:text-ink-5 disabled:cursor-not-allowed"
            >
              <Plus className="w-3 h-3" /> Adicionar
            </button>
          </div>
          <div className="space-y-2">
            {websites.map((w, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="url"
                  value={w}
                  onChange={(e) => updateWebsite(i, e.target.value)}
                  placeholder="https://..."
                  className="flex-1 bg-base border border-white/[0.08] rounded text-sm text-ink-2 px-3 py-2 placeholder:text-ink-5"
                />
                {websites.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeWebsite(i)}
                    className="text-ink-4 hover:text-red-400"
                    aria-label="Remover site"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end pt-2 border-t border-white/[0.06]">
          <button
            type="submit"
            disabled={saving}
            className="bg-brand-600 hover:bg-brand-700 disabled:bg-surface-3 text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar alterações
          </button>
        </div>
      </form>
    </div>
  );
}
